import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Coins, Menu, X } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { StoreLink } from "@/components/landing/StoreLinks";
import { LangFlags } from "@/components/landing/LangFlags";

export const Logo = ({ testId = "header-logo", big = false, compact = false }) => {
  const { t } = useLang();
  const onHome = useLocation().pathname === "/";
  const Tag = onHome ? "a" : Link;
  const target = onHome ? { href: "#top" } : { to: "/" };
  return (
    <Tag {...target} data-testid={testId} className="inline-flex items-center gap-2.5 md:gap-3.5 group min-w-0">
      <img
        src="/brand/logo-256.webp"
        alt={t.logoAlt}
        className={`${big ? "w-28 h-28" : "w-14 h-14 md:w-[76px] md:h-[76px]"} shrink-0 rounded-full transition-transform duration-300 group-hover:-rotate-6`}
      />
      <span className={`font-display font-semibold tracking-tight text-ink truncate ${big ? "text-2xl" : "text-lg md:text-2xl"} ${compact ? "hidden sm:inline" : ""}`}>
        Trollfossjakten
      </span>
    </Tag>
  );
};

const NAV = ["map", "trolls", "seasons", "pricing", "faq"];

export default function Header() {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-[background-color,box-shadow] duration-300 ${
        scrolled ? "bg-parchment/90 backdrop-blur-lg shadow-[0_1px_0_rgba(46,42,37,0.08)]" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-2 md:py-2.5 grid items-center gap-x-4 gap-y-1 grid-cols-[1fr_auto] [grid-template-areas:'flags_flags'_'logo_actions'] lg:grid-cols-[1fr_auto_1fr] lg:[grid-template-areas:'logo_flags_actions'_'logo_nav_actions']">
        <div className="[grid-area:logo] min-w-0">
          <Logo />
        </div>
        <LangFlags className="[grid-area:flags] justify-self-center" />
        <nav className="[grid-area:nav] hidden lg:flex items-center gap-7 text-sm font-bold text-ink-muted justify-self-center">
          {NAV.map((k) => (
            <a key={k} href={`#${k}`} data-testid={`nav-${k}`} className="hover:text-moss transition-colors whitespace-nowrap">
              {t.nav[k]}
            </a>
          ))}
          <Link to="/hosts" data-testid="nav-hosts" className="inline-flex items-center gap-1.5 rounded-full bg-amber/15 text-amber-dark px-3 py-1 hover:bg-amber/25 transition-colors whitespace-nowrap">
            <Coins className="w-3.5 h-3.5" /> {t.nav.hosts}
          </Link>
        </nav>
        <div className="[grid-area:actions] justify-self-end flex items-center gap-2 md:gap-3">
          <StoreLink
            testId="header-get-app"
            className="hidden md:inline-flex rounded-full bg-moss hover:bg-moss-dark text-parchment px-5 py-2.5 text-sm font-extrabold transition-colors whitespace-nowrap"
          >
            {t.nav.getApp}
          </StoreLink>
          <button
            type="button"
            data-testid="mobile-menu-toggle"
            aria-label={t.a11y.menu}
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-10 h-10 grid place-items-center rounded-full border border-line text-ink"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {open && (
        <nav data-testid="mobile-menu" className="lg:hidden border-t border-line bg-parchment px-5 py-4 flex flex-col gap-1">
          {NAV.map((k) => (
            <a key={k} href={`#${k}`} onClick={() => setOpen(false)} data-testid={`mobile-nav-${k}`} className="py-2.5 font-bold text-ink">
              {t.nav[k]}
            </a>
          ))}
          <Link to="/hosts" onClick={() => setOpen(false)} data-testid="mobile-nav-hosts" className="py-2.5 font-bold text-amber-dark inline-flex items-center gap-2">
            <Coins className="w-4 h-4" /> {t.nav.hosts}
          </Link>
        </nav>
      )}
    </header>
  );
}
